import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase"; // Import auth and db for Firestore
import { collection, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { updateDocument, getDocument } from "../../firestore";
import Confetti from "react-confetti"; // Import Confetti
import { MdOutlineRestartAlt } from "react-icons/md";
import Popup from "../Popup";
import "./MatchingGame.css";
import GameOverScreen from "../GameOverScreen";

const MatchingGame = () => {
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [questionStatus, setQuestionStatus] = useState({});
  const [lives, setLives] = useState(3);
  const [score, setScore] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1); // Track current level
  const [showLevelComplete, setShowLevelComplete] = useState(false); // Track level completion
  const [levels, setLevels] = useState([]); // Store questions for all levels
  const [showPopup, setShowPopup] = useState(false);
  const [popupCooldown, setPopupCooldown] = useState(false);
  const uid = auth.currentUser ? auth.currentUser.uid : null;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "questions 2.0"));
        const questions = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((q) => q.type === "Multiple Choice")
          .map((q) => ({
            questionId: q.id,
            question: q.question,
            answer: q.correctAnswer,
            category: q.category || "General",
          }));

        // Split questions into levels of 5 questions each
        const levelsData = [];
        for (let i = 0; i < questions.length; i += 5) {
          levelsData.push(questions.slice(i, i + 5));
        }
        setLevels(levelsData);
      } catch (error) {
        console.error("Error fetching questions: ", error);
      }
    };

    fetchQuestions();
  }, []);

  useEffect(() => {
    const popupInterval = setInterval(() => {
      if (!popupCooldown) {
        setShowPopup(true);
        setPopupCooldown(true);
      }
    }, 15000);

    return () => clearInterval(popupInterval);
  }, [popupCooldown]);

  if (levels.length === 0 || currentLevel > levels.length) {
    return <div>Loading...</div>;
  }

  const questions = levels[currentLevel - 1]; // Get current level questions

  // Shuffle answers for display
  const shuffledAnswers = [...questions.map((q) => q.answer)].sort(
    () => 0.5 - Math.random()
  );

  const updateCategoryStats = async (category, isCorrect) => {
    if (!uid || !category) return;

    try {
      const categoryDocRef = doc(db, `questionstat/${uid}/${category}/stats`);
      const categoryDocSnap = await getDoc(categoryDocRef);

      let totalQuestions = 0,
        correctAnswers = 0,
        wrongAnswers = 0;

      if (categoryDocSnap.exists()) {
        const data = categoryDocSnap.data();
        totalQuestions = data.totalQuestions || 0;
        correctAnswers = data.correctAnswers || 0;
        wrongAnswers = data.wrongAnswers || 0;
      }

      const updates = {
        totalQuestions: totalQuestions + 1,
        correctAnswers: isCorrect ? correctAnswers + 1 : correctAnswers,
        wrongAnswers: isCorrect ? wrongAnswers : wrongAnswers + 1,
      };

      await setDoc(categoryDocRef, updates, { merge: true });
      console.log(`Category stats updated for ${category}:`, updates);
    } catch (error) {
      console.error("Error updating category stats:", error);
    }
  };

  const handleQuestionSelect = (questionId) => {
    setSelectedQuestionId(questionId);
  };

  const handleAnswerSelect = (answer) => {
    if (!selectedQuestionId) return;

    const selectedQuestion = questions.find(
      (q) => q.questionId === selectedQuestionId
    );
    const correctAnswer = selectedQuestion.answer;
    const category = selectedQuestion.category;

    if (answer === correctAnswer) {
      setQuestionStatus((prev) => ({
        ...prev,
        [selectedQuestionId]: "correct",
      }));
      setScore((prevScore) => prevScore + 50);

      // Update stats for the correct answer
      updateCategoryStats(category, true);

      const allCorrect = questions.every(
        (q) =>
          questionStatus[q.questionId] === "correct" ||
          q.questionId === selectedQuestionId
      );
      if (allCorrect) setShowLevelComplete(true); // Show level complete popup

      setSelectedQuestionId(null);
    } else {
      setLives((prevLives) => prevLives - 1);
      setScore((prevScore) => prevScore - 20);

      // Update stats for the wrong answer
      updateCategoryStats(category, false);

      if (lives - 1 <= 0) {
        setIsGameOver(true); // Game Over
        gameOverDBUpdate();
      }

      setQuestionStatus((prev) => ({
        ...prev,
        [selectedQuestionId]: "incorrect",
      }));
    }
  };

  async function gameOverDBUpdate() {
    const queryParams = new URLSearchParams(window.location.search);
    var gameId = queryParams.get("id");
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    let currentScore = currentLevel * 100;
    const gameplayData = {
      lives: lives,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, gameId, gameplayData);
    await totalGameDataUpdate(currentScore);
  }

  async function totalGameDataUpdate(score) {
    const subCollectionPath = `gameplay`;
    const userId = auth.currentUser.uid;
    var docData = await getDocument(subCollectionPath, userId);
    let oldScore = docData.score;
    let total_games = docData.games_played + 1;
    let currentScore = score + oldScore;
    const gameplayData = {
      games_played: total_games,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, userId, gameplayData);
  }

  const handleNextLevel = () => {
    setCurrentLevel((prev) => prev + 1); // Advance to next level
    setQuestionStatus({}); // Reset question statuses
    setShowLevelComplete(false); // Hide popup
  };

  const handleRestart = () => {
    setSelectedQuestionId(null);
    setQuestionStatus({});
    setLives(3); // Reset lives
    setScore(0);
    setIsGameOver(false);
    setCurrentLevel(1); // Reset to level 1
  };

  const handleExit = () => {
    window.location.href = "/games"; // Game hub route
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setTimeout(() => setPopupCooldown(false), 15000);
  };

  const renderHearts = () => {
    return Array.from({ length: 3 }).map((_, index) => (
      <span
        key={index}
        className={`text-red-500 text-2xl ${
          index < lives ? "opacity-100" : "opacity-30"
        }`}
      >
        ❤️
      </span>
    ));
  };

  return (
    <div className="min-h-screen bg-[var(--background-color)] py-8 px-4 sm:px-6 lg:px-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        {/* Page Title with Gradient */}
        <h1 className="font-['Jersey_25',sans-serif] text-4xl md:text-5xl font-bold text-center mb-10 text-yellow-500 bg-gradient-to-r from-[var(--primary-color)] via-[var(--interactive-color)] to-[var(--accent-color)] text-transparent bg-clip-text transform transition-all">
          The Matching Game
        </h1>

        {/* Game Stats and Controls */}
        <div className="bg-[var(--surface-color)] rounded-2xl shadow-lg overflow-hidden border border-[var(--border-color)]">
          <div className="bg-[var(--primary-color)] py-4 px-6">
            <div className="flex items-center">
              <div className="text-white text-3xl w-full flex justify-between items-center p-4">
                <div className="flex items-center gap-6">
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Score: {score}
                  </h1>
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Lives: {renderHearts()}
                  </h1>
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Level: {currentLevel}
                  </h1>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                    onClick={handleRestart}
                  >
                    <MdOutlineRestartAlt className="mr-2 text-xl" />
                    Restart
                  </button>

                  <button
                    className="group relative inline-flex items-center px-6 py-3 bg-[var(--error-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--error-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                    onClick={handleExit}
                  >
                    Exit
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center p-6">
            <div className="bank-container">
              <div className="question-bank">
                <h2>Question Bank</h2>
                {questions.map((question) => (
                  <button
                    key={question.questionId}
                    onClick={() => handleQuestionSelect(question.questionId)}
                    className={`button-wrapper ${
                      questionStatus[question.questionId]
                    }`}
                  >
                    {question.question}
                  </button>
                ))}
              </div>

              <div className="answer-bank">
                <h2>Answer Bank</h2>
                {shuffledAnswers.map((answer, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(answer)}
                    disabled={
                      !selectedQuestionId ||
                      questionStatus[
                        questions.find((q) => q.answer === answer)?.questionId
                      ] === "correct"
                    }
                    className={`button-wrapper ${
                      questionStatus[
                        questions.find((q) => q.answer === answer)?.questionId
                      ] === "correct"
                        ? "correct"
                        : ""
                    }`}
                  >
                    {answer}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Popup and Game Over Screens */}
        {showPopup && (
          <Popup
            onClose={handlePopupClose}
            score={score}
            setScore={setScore}
            lives={lives}
            setLives={setLives}
            useLives={true}
          />
        )}

        {showLevelComplete && (
          <>
            <Confetti gravity={1} />
            <div className="level-complete-overlay"></div>
            <div className="level-complete-popup1">
              <h2>Level {currentLevel} Complete!</h2>
              <button
                onClick={handleNextLevel}
                disabled={currentLevel === levels.length}
              >
                {currentLevel === levels.length ? "Finish Game" : "Next Level"}
              </button>
            </div>
          </>
        )}

        {isGameOver && (
          <GameOverScreen
            score={score}
            onRestart={handleRestart}
            onExit={handleExit}
          />
        )}
      </div>
    </div>
  );
};

export default MatchingGame;
